import classNames from 'classnames';
import config from 'config';
import { motion } from 'framer-motion';
import { throttle } from 'lodash-es';
import React, { useCallback, useState } from 'react';
import noop from 'utils/noop';
import styles from './Overlay.module.scss';
import RootLayout from './ui-blocks/layout/RootLayout';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  offsetNavbar?: boolean;
  flexColumns?: boolean;
  /** Render a header? Default to sticky on scroll via `stickyHeader` */
  header?: React.ReactNode;
  /** Should `props.header` become sticky & cast a shadow when scrolled? */
  stickyHeader?: boolean;
}

export const Overlay = (props: Props) => {
  const [isSticky, setAsSticky] = useState(false);

  const {
    children,
    offsetNavbar = false,
    flexColumns = true,
    header,
    stickyHeader = true,
    ...rest
  } = props;

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const willBeSticky = event.currentTarget?.scrollTop > 0;
      if (willBeSticky !== isSticky) setAsSticky(willBeSticky);
    },
    [isSticky]
  );

  const debouncedHandleScroll = throttle(e => handleScroll(e), 10);

  return (
    <motion.div
      initial={{
        translateY: -25,
      }}
      animate={{
        translateY: 0,
        transition: {
          duration: config.animation.duration,
        },
      }}
      className={styles.outerContainer}
      onScroll={stickyHeader ? debouncedHandleScroll : noop}
    >
      <RootLayout
        flexColumns={flexColumns}
        offsetNavbar={offsetNavbar}
        {...rest}
        className={classNames({ [styles.offsetNavbar]: offsetNavbar })}
        autoOverflow
      >
        {!!header && (
          <div
            className={classNames(styles.header, {
              [styles.enableSticky]: stickyHeader,
              [styles.currentlySticky]: isSticky,
            })}
          >
            {header}
          </div>
        )}
        {children}
      </RootLayout>
    </motion.div>
  );
};

export default Overlay;
